import { IosShare } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  Link as RouterLink,
  Outlet,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Home as HomeIcon } from "@mui/icons-material";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { PolicyDownloadButton } from "~/components/policy-download-button";
import { Flex } from "~/components/Flex";
import { TabsVariant, VariantIcon } from "~/components/ui-library";
import { copyToClipboard } from "~/lib/clipboard";
import { PolicyIdToMrn, PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { Space } from "~/lib/types";
import { TrustLevel, useGetPolicyQuery } from "~/operations";
import { PolicyAssignButton } from "./components/PolicyAssignButton";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { Caption } from "./components/Caption";
import { ShowMoreSummary } from "~/components/ShowMore/ShowMoreSummary";
import { setDocumentTitle } from "~/lib/commonUtils";
import { PolicyDeleteButton } from "~/components/policy-gql/PolicyDeleteButton";
import { CanDeletePolicy } from "~/lib/iam/actions/cnspec/policy/PolicyHub/DeletePolicy";
import { CanAssign } from "~/lib/iam/actions/cnspec/policy/PolicyResolver/Assign";
import { EmailIcon, LinkIcon } from "~/icons";

export type PoliciesAddDetailPageProps = {
  space: Space;
};

export function PoliciesAddDetailPage({ space }: PoliciesAddDetailPageProps) {
  const { policyId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const shareOpen = Boolean(shareAnchorEl);

  const mrn = PolicyIdToMrn(policyId);
  const selectedVariantId = searchParams.get("variant");

  const { data, loading, error, refetch } = useGetPolicyQuery({
    variables: { input: { mrn, spaceMrn: space.mrn } },
    skip: !policyId,
  });

  const loadingFailed = !policyId || error || !data?.policy;
  const policy = data?.policy;
  const isCustom = policy?.trustLevel === TrustLevel.Private;

  const variants = [
    {
      label: "Overall",
      value: "",
      icon: "",
    },
    ...(
      policy?.variantPlatformFilters?.map((variant) => ({
        label: variant?.title || "",
        value: variant?.id || "",
        icon: <VariantIcon type={variant?.icon || ""} />,
      })) || []
    ).sort((a, b) => (a.label > b.label ? 1 : -1)),
  ];

  const generateHref = (tab: string): string => {
    return `/space/findings/policies/add/${PolicyMrnToURIEncodedId(policy?.mrn || "")}/${tab}?spaceId=${space.id}`;
  };

  const tabList: TabListItem[] = [
    {
      label: "Checks",
      to: generateHref("checks"),
      route: "/checks",
    },
    {
      label: "Properties",
      to: generateHref("properties"),
      route: "/properties",
    },
  ];

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "checks",
  );

  const shareLink = window.location.href;
  const shareEmailLink = `mailto:hello@mondoo.com?subject=${encodeURIComponent("Take a look at this policy")}&body=${encodeURIComponent(shareLink)}`;

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?spaceId=${space.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/findings/policies"
      component={RouterLink}
      to={`/space/findings/policies?spaceId=${space.id}`}
    >
      Policies
    </Link>,
    <Link
      key="/space/findings/policies/add"
      component={RouterLink}
      to={`/space/findings/policies/add?spaceId=${space.id}`}
    >
      Add Policies
    </Link>,
    <Typography key="/space/findings/policies/add/policyId">
      {policy?.name}
    </Typography>,
  ];

  setDocumentTitle([policy?.name, "Add Policies", "Policies", "Security"]);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {policy && (
        <>
          <Box
            id="policy-header"
            mb={3}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Flex flexDirection="column">
              <Typography
                variant="h4"
                component="h1"
                fontWeight={700}
                textTransform="uppercase"
                sx={{ mb: 1 }}
              >
                {policy.name}
              </Typography>
              <Caption
                trustLevel={policy.trustLevel}
                version={policy.version}
                authors={policy.authors}
              />
            </Flex>
            <Flex id="policy-actions" alignItems="center" gap={4}>
              <Tooltip title="Share" placement="top" arrow>
                <IconButton onClick={(e) => setShareAnchorEl(e.currentTarget)}>
                  <IosShare />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={shareAnchorEl}
                open={shareOpen}
                onClose={() => setShareAnchorEl(null)}
                onClick={() => setShareAnchorEl(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <MenuItem onClick={() => copyToClipboard(shareLink)}>
                  <ListItemIcon>
                    <LinkIcon fontSize="inherit" />
                  </ListItemIcon>
                  <ListItemText>Copy link</ListItemText>
                </MenuItem>
                <MenuItem component="a" href={shareEmailLink}>
                  <ListItemIcon>
                    <EmailIcon fontSize="inherit" />
                  </ListItemIcon>
                  <ListItemText>Email</ListItemText>
                </MenuItem>
              </Menu>
              <PolicyDownloadButton policy={policy} size="small" iconOnly />
              <Divider orientation="vertical" flexItem />
              {isCustom && (
                <CanDeletePolicy>
                  <PolicyDeleteButton
                    space={space}
                    policyMrn={policy.mrn}
                    isCustom={isCustom}
                  />
                </CanDeletePolicy>
              )}
              <CanAssign>
                <PolicyAssignButton
                  scope={space}
                  policyMrn={policy.mrn}
                  assigned={policy.assigned}
                  action={policy.action}
                  onChange={() => {
                    refetch();
                  }}
                  advanced
                />
              </CanAssign>
            </Flex>
          </Box>
          {variants.length > 1 && (
            <TabsVariant
              options={variants}
              selectedTab={selectedVariantId || ""}
              onTabChange={(variantId) => {
                variantId.length > 0
                  ? searchParams.set("variant", variantId)
                  : searchParams.delete("variant");
                setSearchParams(searchParams);
              }}
              mb={3}
            />
          )}
          {policy.docs && (
            <ShowMoreSummary
              maxHeight={72}
              text={policy.docs}
              externalLinksInNew
            />
          )}
        </>
      )}

      {loading && <LoadingPage what="Policy" />}
      {!loading && loadingFailed && <LoadingFailedPage what="Policy" />}
      {!loading && policy && (
        <Box>
          <TabNavigation {...{ id: "policy-tabs", tabList, currentTab }} />
          <Outlet
            context={{
              space,
              policyId,
              variantPlatformFilter: selectedVariantId,
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export type PolicyAddDetailOutletContextType = {
  space: Space;
  policyId: string;
  variantPlatformFilter: string | null;
};

export function usePolicyAddDetailOutletContext() {
  return useOutletContext<PolicyAddDetailOutletContextType>();
}
